import React, { useEffect } from 'react';
import { Product } from '../../../../app/models/Product';
import { Place } from '../../../../app/models/Place';
import { Unit } from '../../../../app/models/Unit';
import { Service } from '../../../../app/models/Service';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { setCleanTextAreaError } from '../../../../utils/utils';
import { Currency } from '../../../../app/models/Currency';
import { TransportationQuoteDetailDataForm } from '../../../../app/models/Quote';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { QuoteDetailFormTypeEnum } from '../../../../app/shared/enums';
import { RouteSegment } from '../../../../app/models/RouteSegment';
import { QuoteRouteSegment } from '../../../../app/models/QuoteRouteSegment';
import LazyLoading from '../../../../components/LazyLoading';

interface QuoteDetailsProps {
    quoteDetailForm: TransportationQuoteDetailDataForm;
    setQuoteDetailForm: (quoteDetail: TransportationQuoteDetailDataForm) => void;
    products: Product[];
    quantityUnits: Unit[];
    currencies: Currency[];
    origins: Place[];
    destinies: Place[];
    services: Service[];
    routeSegments: RouteSegment[];
    quoteRouteSegments: QuoteRouteSegment[];
    errorFields?: Record<string, unknown>;
    fetching: boolean;
    disableUpdate?: boolean;
}

const QuoteDetailForm = ({
    quoteDetailForm,
    setQuoteDetailForm,
    products,
    quantityUnits,
    currencies,
    origins,
    destinies,
    services,
    routeSegments,
    errorFields,
    quoteRouteSegments,
    fetching,
    disableUpdate = false
}: QuoteDetailsProps) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    void origins;
    void destinies;
    void quoteRouteSegments;

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    // const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    //     const obj = {quote_route_segment_id : event.target.value}
    //     setQuoteDetailForm({ ...quoteDetailForm, ...obj });
    //     setSelectedRowId(event.target.value);

    // };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = event.target;

        const normalizedValue = type === 'number' && value === '' ? '' : value;

        setQuoteDetailForm({
            ...quoteDetailForm,
            [name]: normalizedValue
        });
    };

    const handleChangeText = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target;

        setQuoteDetailForm({
            ...quoteDetailForm,
            [name]: value
        });
    };

    const handleChangeSelectTwo = (
        name: keyof TransportationQuoteDetailDataForm,
        value: number | string | null
    ) => {
        setQuoteDetailForm({
            ...quoteDetailForm,
            [name]: value
        });
    };

    type SelectTwoValue = { value: number | string | null } | null;

    const createSelectTwoChangeHandler = (
        name: keyof TransportationQuoteDetailDataForm
    ) =>
        (option: SelectTwoValue) => {
            handleChangeSelectTwo(name, option?.value ?? null);
        };

    const formatCurrencyValue = (
        rawValue: string,
        currencyId: number | string | null | undefined
    ) => {
        let rate = String(rawValue ?? '');

        const foundCurrency = currencies.find((item) => item.id === currencyId);
        const currencyName = foundCurrency ? foundCurrency.name : '';

        if (rate && currencyName === 'CLP') {
            const nonDigitIndex = rate.search(/\D/);

            if (nonDigitIndex !== -1) {
                rate = rate.substring(0, nonDigitIndex);
            }
        }

        return rate;
    };

    const handleRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        const currencyFieldByRate: Record<string, keyof TransportationQuoteDetailDataForm> = {
            rate: 'currency_id',
            rate_runner: 'currency_rate_runner_id',
            rate_transporter: 'currency_rate_transporter_id'
        };

        const currencyField = currencyFieldByRate[name];
        const currencyId = currencyField ? quoteDetailForm[currencyField] : undefined;

        const formattedValue =
            currencyField !== undefined
                ? formatCurrencyValue(value, currencyId as number | string | null | undefined)
                : value;

        setQuoteDetailForm({
            ...quoteDetailForm,
            [name]: formattedValue
        });
    };

    if (!quoteDetailForm) return null;

    const selectedService = services.find((s) => String(s.id) === String(quoteDetailForm.service_id));
    const isTransportMaterial =
        selectedService?.quote_detail_form_type === QuoteDetailFormTypeEnum.TRANSPORT;

    return (
        <>
            {!disableUpdate && (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Servicio</label>
                    <div className="col-md-9">
                        <SelectTwo
                            name="service_id"
                            id="service_id"
                            inputValue={quoteDetailForm.service_id}
                            hasError={fieldHasError('service_id') !== ''}
                            options={SelectTwoMapperOptions(services)}
                            onChange={createSelectTwoChangeHandler('service_id')}
                            placeholder={'Seleccione un servicio'}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('service_id')}
                        </div>
                    </div>
                </div>
            )}
            {!disableUpdate && (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Producto</label>
                    <div className="col-md-9">
                        <SelectTwo
                            name="product_id"
                            id="product_id"
                            inputValue={quoteDetailForm.product_id}
                            hasError={fieldHasError('product_id') !== ''}
                            options={SelectTwoMapperOptions(products)}
                            onChange={createSelectTwoChangeHandler('product_id')}
                            placeholder={'Seleccione un producto'}
                            onFocus={() => onFocusRemove('product_id')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('product_id')}
                        </div>
                    </div>
                </div>
            )}

            {!disableUpdate && (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Fecha de Despacho</label>
                    <div className="col-md-9">
                        <input
                            type="date"
                            className={`form-control ${fieldHasError('first_dispatch_date')}`}
                            name="first_dispatch_date"
                            id="first_dispatch_date"
                            value={quoteDetailForm.first_dispatch_date}
                            onChange={handleChange}
                            placeholder="Ingrese una fecha de despacho"
                            onFocus={() => onFocusRemove('first_dispatch_date')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('first_dispatch_date')}
                        </div>
                    </div>
                </div>
            )}
            {!disableUpdate && isTransportMaterial && (
                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Descripción</label>
                    <div className="col-md-9">
                        <textarea
                            className={`form-control ${fieldHasError('description')}`}
                            name="description"
                            id="description"
                            value={quoteDetailForm.description}
                            onChange={handleChangeText}
                            placeholder="Ingrese una descripción"
                            onFocus={setCleanTextAreaError}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('description')}
                        </div>
                    </div>
                </div>
            )}
            <hr />
            {fetching ? (
                <LazyLoading height={300} />
            ) : (
                <>
                    {!disableUpdate && isTransportMaterial && (
                        <div className="mt-4 mb-2 row">
                            <label className="col-md-3 col-form-label">Tramo</label>
                            <div className="col-md-9">
                                <SelectTwo
                                    name="route_segment_id"
                                    id="route_segment_id"
                                    inputValue={quoteDetailForm.route_segment_id}
                                    hasError={fieldHasError('route_segment_id') !== ''}
                                    options={SelectTwoMapperOptions(routeSegments)}
                                    onChange={createSelectTwoChangeHandler('route_segment_id')}
                                    placeholder={'Seleccione un tramo'}
                                    onFocus={() => onFocusRemove('route_segment_id')}
                                />
                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                    {fieldErrorMessage('route_segment_id')}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mb-2 row">
                        <label className="col-md-3 col-form-label">Cantidad</label>
                        <div className="col-md-9">
                            <div className="row g-1">
                                <div className="col-6">
                                    <input
                                        type="number"
                                        name="quantity"
                                        id="quantity"
                                        onFocus={() => onFocusRemove('quantity')}
                                        className={`form-control ${fieldHasError('quantity')}`}
                                        value={quoteDetailForm.quantity}
                                        onChange={handleChange}
                                        placeholder="Ingrese cantidad"
                                    />
                                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                                        {fieldErrorMessage('quantity')}
                                    </div>
                                </div>

                                <div className="col-6">
                                    <SelectTwo
                                        name="quantity_unit_id"
                                        id="quantity_unit_id"
                                        hasError={fieldHasError('quantity_unit_id') !== ''}
                                        inputValue={quoteDetailForm.quantity_unit_id}
                                        options={SelectTwoMapperOptions(quantityUnits)}
                                        onChange={createSelectTwoChangeHandler('quantity_unit_id')}
                                        placeholder={'medida'}
                                        onFocus={() => onFocusRemove('quantity_unit_id')}
                                        disabled={disableUpdate}
                                    />
                                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                                        {fieldErrorMessage('quantity_unit_id')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {!disableUpdate && (
                        <>
                            <div className=" row">
                                <label className="col-md-3 col-form-label">Tarifa de venta</label>
                                <div className="col-md-9 ">
                                    <div className="row g-1">
                                        <div className="col-6">
                                            <input
                                                type="number"
                                                name="rate"
                                                className={`form-control ${fieldHasError('rate')}`}
                                                id="rate"
                                                onFocus={() => onFocusRemove('rate')}
                                                value={quoteDetailForm.rate}
                                                onChange={handleRateChange}
                                                placeholder="Ingrese tarifa"
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('rate')}
                                            </div>
                                        </div>

                                        <div className="col-3">
                                            <SelectTwo
                                                name="currency_id"
                                                id="currency_id"
                                                inputValue={quoteDetailForm.currency_id}
                                                options={SelectTwoMapperOptions(currencies)}
                                                hasError={fieldHasError('currency_id') !== ''}
                                                onChange={createSelectTwoChangeHandler('currency_id')}
                                                placeholder={'moneda'}
                                                onFocus={() => onFocusRemove('currency_id')}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('currency_id')}
                                            </div>
                                        </div>

                                        <div className="col-3">
                                            <SelectTwo
                                                name="rate_unit_id"
                                                id="rate_unit_id"
                                                inputValue={quoteDetailForm.rate_unit_id}
                                                options={SelectTwoMapperOptions(quantityUnits)}
                                                hasError={fieldHasError('rate_unit_id') !== ''}
                                                onChange={createSelectTwoChangeHandler('rate_unit_id')}
                                                onFocus={() => onFocusRemove('rate_unit_id')}
                                                placeholder={'unidad'}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('rate_unit_id')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {!disableUpdate && isTransportMaterial && (
                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Distancia KM</label>
                            <div className="col-md-9">
                                <input
                                    name="distance"
                                    id="distance"
                                    type="number"
                                    className={`form-control ${fieldHasError('distance')}`}
                                    value={quoteDetailForm.distance}
                                    onChange={handleChange}
                                    onFocus={() => onFocusRemove('distance')}
                                    placeholder="Ingrese distancia del tramo"
                                />
                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                    {fieldErrorMessage('distance')}
                                </div>
                            </div>
                        </div>
                    )}
                    {!disableUpdate && isTransportMaterial && (
                        <div className="mb-2 row">
                            <label className="col-md-3 col-form-label">Distancia acordada KM</label>
                            <div className="col-md-9">
                                <input
                                    name="agreed_distance"
                                    id="agreed_distance"
                                    type="number"
                                    className={`form-control ${fieldHasError('agreed_distance')}`}
                                    value={quoteDetailForm.agreed_distance}
                                    onChange={handleChange}
                                    onFocus={() => onFocusRemove('agreed_distance')}
                                    placeholder="Ingrese distancia acordada del tramo"
                                />
                                <div className="invalid-feedback" style={{ display: 'flex' }}>
                                    {fieldErrorMessage('agreed_distance')}
                                </div>
                            </div>
                        </div>
                    )}
                    {!disableUpdate && isTransportMaterial && (
                        <>
                            <div className="mb-2 row">
                                <label className="col-md-3 col-form-label">Tarifa Runner</label>
                                <div className="col-md-9 ">
                                    <div className="row g-1">
                                        <div className="col-6">
                                            <input
                                                type="number"
                                                name="rate_runner"
                                                className={`form-control ${fieldHasError(
                                                    'rate_runner'
                                                )}`}
                                                id="rate_runner"
                                                onFocus={() => onFocusRemove('rate_runner')}
                                                value={quoteDetailForm.rate_runner}
                                                onChange={handleRateChange}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('rate_runner')}
                                            </div>
                                        </div>

                                        <div className="col-3">
                                            <SelectTwo
                                                name="currency_rate_runner_id"
                                                id="currency_rate_runner_id"
                                                inputValue={quoteDetailForm.currency_rate_runner_id}
                                                options={SelectTwoMapperOptions(currencies)}
                                                hasError={
                                                    fieldHasError('currency_rate_runner_id') !== ''
                                                }
                                                onChange={createSelectTwoChangeHandler('currency_rate_runner_id')}
                                                onFocus={() =>
                                                    onFocusRemove('currency_rate_runner_id')
                                                }
                                                placeholder={'moneda'}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('currency_rate_runner_id')}
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <SelectTwo
                                                name="quantity_unit_rate_runner_id"
                                                id="quantity_unit_rate_runner_id"
                                                inputValue={
                                                    quoteDetailForm.quantity_unit_rate_runner_id
                                                }
                                                options={SelectTwoMapperOptions(quantityUnits)}
                                                hasError={
                                                    fieldHasError(
                                                        'quantity_unit_rate_runner_id'
                                                    ) !== ''
                                                }
                                                onChange={createSelectTwoChangeHandler('quantity_unit_rate_runner_id')}
                                                onFocus={() =>
                                                    onFocusRemove('quantity_unit_rate_runner_id')
                                                }
                                                placeholder={'unidad'}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('quantity_unit_rate_runner_id')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {!disableUpdate && isTransportMaterial && (
                        <>
                            <div className="mb-2 row">
                                <label className="col-md-3 col-form-label">
                                    Tarifa Transportista
                                </label>
                                <div className="col-md-9 ">
                                    <div className="row g-1">
                                        <div className="col-6">
                                            <input
                                                type="number"
                                                name="rate_transporter"
                                                className={`form-control ${fieldHasError(
                                                    'rate_transporter'
                                                )}`}
                                                id="rate_transporter"
                                                onFocus={() => onFocusRemove('rate_transporter')}
                                                value={quoteDetailForm.rate_transporter}
                                                onChange={handleRateChange}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('rate_transporter')}
                                            </div>
                                        </div>

                                        <div className="col-3">
                                            <SelectTwo
                                                name="currency_rate_transporter_id"
                                                id="currency_rate_transporter_id"
                                                inputValue={
                                                    quoteDetailForm.currency_rate_transporter_id
                                                }
                                                options={SelectTwoMapperOptions(currencies)}
                                                hasError={
                                                    fieldHasError(
                                                        'currency_rate_transporter_id'
                                                    ) !== ''
                                                }
                                                onChange={createSelectTwoChangeHandler('currency_rate_transporter_id')}
                                                onFocus={() =>
                                                    onFocusRemove('currency_rate_transporter_id')
                                                }
                                                placeholder={'moneda'}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage('currency_rate_transporter_id')}
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <SelectTwo
                                                name="quantity_unit_rate_transporter_id"
                                                id="quantity_unit_rate_transporter_id"
                                                inputValue={
                                                    quoteDetailForm.quantity_unit_rate_transporter_id
                                                }
                                                options={SelectTwoMapperOptions(quantityUnits)}
                                                hasError={
                                                    fieldHasError(
                                                        'quantity_unit_rate_transporter_id'
                                                    ) !== ''
                                                }
                                                onChange={createSelectTwoChangeHandler('quantity_unit_rate_transporter_id')}
                                                onFocus={() =>
                                                    onFocusRemove(
                                                        'quantity_unit_rate_transporter_id'
                                                    )
                                                }
                                                placeholder={'unidad'}
                                            />
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'flex' }}
                                            >
                                                {fieldErrorMessage(
                                                    'quantity_unit_rate_transporter_id'
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default QuoteDetailForm;
